import { ref, onBeforeUnmount } from 'vue'
import PullToRefresh from 'pulltorefreshjs'
import { useI18n } from 'vue-i18n'

/**
 * Composable for pull-to-refresh functionality
 * Replaces the Vue 2 PullToRefresh mixin
 *
 * @returns {Object} Pull-to-refresh state and methods
 */
export function usePullToRefresh() {
  const { t } = useI18n()
  const ptr = ref(null)

   /**
   * Destroy the pull-to-refresh instance
   */
   const ptrDestroy = () => {
    if (ptr.value) {
      ptr.value.destroy()
      ptr.value = null
    }
  }

  /**
   * Method that should be overridden by consuming component
   * to provide refresh action
   */
  const ptrOnRefresh = () => {
    throw new Error('No refresh action defined')
  }

  // Clean up when component is unmounted
  onBeforeUnmount(() => {
    ptrDestroy()
  })

  /**
   * Initialize pull-to-refresh functionality
   * @param {Object} opts - Configuration options for PullToRefresh
   */
  const ptrInit = (opts = {}) => {
    ptrDestroy()

    ptr.value = PullToRefresh.init({
      mainElement: '#app .v-main .v-container > div',
      onRefresh: () => {
        try {
          ptrOnRefresh()
        } catch (error) {
          // throwing would bug out the ptr overlay
          console.error(error)
        }
      },
      instructionsPullToRefresh: t('common.ptr.pull'),
      instructionsReleaseToRefresh: t('common.ptr.release'),
      instructionsRefreshing: t('common.ptr.refreshing'),
      shouldPullToRefresh() {
        // don't activate if not at top of scroll element
        if (opts?.mainElement && this.mainElement.scrollTop) return false
        // if default element, we fall back to window scroll
        if (window.scrollY) return false

        // Don't activate when modals are open
        return !document.documentElement.classList.contains('overflow-y-hidden')
      },
      ...opts,
    })
  }

  return {
    ptr,
    ptrInit,
    ptrDestroy,
    ptrOnRefresh
  }
}
